import { useState } from 'react'

import { ExpenseBar, ExpenseRing } from '../../components/ExpenseChart'
import {
  anteilBezug,
  anteilProzent,
  ringSegments,
  sumTotal,
  type CategorySum,
} from '../../lib/expenseChart'
import { money, num } from '../../lib/format'

/**
 * Zwei Sichten auf dieselben Zahlen: Anteil und Grössenordnung.
 *
 * Gezeichnet wird mit denselben Bauteilen wie im Buch (components/ExpenseChart)
 * und gerechnet mit derselben Quelle (lib/expenseChart). Früher stand hier eine
 * Diagrammbibliothek und im Buch eine zweite, handgezeichnete Fassung - zwei
 * Umsetzungen derselben Sache, die auseinanderlaufen konnten, ohne dass es
 * jemandem auffiel.
 *
 * Am Bildschirm kommt dazu, was auf Papier nicht geht: unter dem Zeiger tritt
 * eine Kategorie hervor und ihr Betrag steht in der Ringmitte. Die Zeichnung
 * selbst bleibt dieselbe - das hervorgehobene Segment wächst nicht, sonst
 * zeigte der Ring unter dem Zeiger eine andere Verteilung als im Buch.
 */
export function ExpenseCharts({ data, currency }: { data: CategorySum[]; currency: string }) {
  const [hervor, setHervor] = useState('')

  // Zwei Zahlen mit zwei Aufgaben: gesamt ist die Auskunft in der Ringmitte,
  // bezug der Massstab der Zeichnung. Sie fallen auseinander, sobald eine
  // Kategorie unterm Strich negativ ist - eine Erstattung etwa -, und genau
  // dann rettet der Unterschied den Ring. Siehe anteilBezug.
  const gesamt = sumTotal(data)
  const bezug = anteilBezug(data)
  const segmente = ringSegments(data, bezug)
  const gezeigt = data.find((eintrag) => eintrag.key === hervor)

  return (
    <div className="diagramme">
      <div className="diagramm">
        <ExpenseRing
          segmente={segmente}
          praefix="ring"
          label="Ausgaben je Kategorie"
          wert={num(gezeigt ? gezeigt.value : gesamt)}
          text={gezeigt ? gezeigt.key : currency}
          hervor={hervor}
          onZeigen={setHervor}
        />
      </div>

      <div className="diagramm">
        {/*
          Dieselbe Liste wie auf der Budgetseite des Buchs: Name, Anteilsbalken,
          Prozent, Betrag. Alles, was der Balken zeigt, steht daneben auch als
          Text - die Hervorhebung ist eine Zugabe fuer den Zeiger und nicht der
          einzige Weg zur Zahl.
        */}
        <ul className="anteilsliste">
          {data.map((eintrag) => {
            const anteil = anteilProzent(eintrag.value, bezug)
            return (
              <li
                key={eintrag.key}
                className={hervor === eintrag.key ? 'hervor' : undefined}
                onPointerEnter={() => {
                  setHervor(eintrag.key)
                }}
                onPointerLeave={() => {
                  setHervor('')
                }}
              >
                <span className="name">{eintrag.key}</span>
                <ExpenseBar anteil={anteil} farbe={eintrag.color} className="balken" />
                <span className="anteil">{Math.round(anteil)} %</span>
                <span className="betrag">{money(eintrag.value, currency)}</span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
